import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { uofmApi, UOFM, UOFMSearchParams } from '../../services/uofmApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  FETCH_UOFMS,
  FETCH_UOFM_BY_ID,
  CREATE_UOFM,
  UPDATE_UOFM,
  DELETE_UOFM,
  SEARCH_UOFMS,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/uofmSlice';

// Fetch all UOFMs
function* fetchUOFMsSaga(action: PayloadAction<UOFMSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { pagination, searchParams } = state.uofm;
    
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchParams.search || '',
      active: searchParams.active,
      sortBy: searchParams.sortBy || 'Name',
      sortOrder: searchParams.sortOrder || 'asc',
      ...action.payload,
    };
    
    const response = yield call(uofmApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching UOFMs:', error);
    yield put(setError(error.message || 'Failed to fetch UOFMs'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch UOFM by ID
function* fetchUOFMByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const uofm = yield call(uofmApi.getById, action.payload);
    yield put(setSelectedItem(uofm));
  } catch (error: any) {
    console.error('Error fetching UOFM by ID:', error);
    yield put(setError(error.message || 'Failed to fetch UOFM'));
  } finally {
    yield put(setLoading(false));
  }
}

// Create UOFM
function* createUOFMSaga(action: PayloadAction<Omit<UOFM, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(uofmApi.validation.validateUOFM, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newUOFM = yield call(uofmApi.create, action.payload);
    yield put(addItem(newUOFM));
    yield put(setSuccessMessage('UOFM created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating UOFM:', error);
    yield put(setError(error.message || 'Failed to create UOFM'));
  } finally {
    yield put(setLoading(false));
  }
}

// Update UOFM
function* updateUOFMSaga(action: PayloadAction<{ id: number; uofm: Partial<UOFM> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, uofm } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(uofmApi.validation.validateUOFM, uofm);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedUOFM = yield call(uofmApi.update, id, uofm);
    yield put(updateItem(updatedUOFM));
    yield put(setSuccessMessage('UOFM updated successfully'));
  } catch (error: any) {
    console.error('Error updating UOFM:', error);
    yield put(setError(error.message || 'Failed to update UOFM'));
  } finally {
    yield put(setLoading(false));
  }
}

// Delete UOFM
function* deleteUOFMSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(uofmApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('UOFM deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete UOFM'));
    }
  } catch (error: any) {
    console.error('Error deleting UOFM:', error);
    yield put(setError(error.message || 'Failed to delete UOFM'));
  } finally {
    yield put(setLoading(false));
  }
}

// Search UOFMs
function* searchUOFMsSaga(action: PayloadAction<UOFMSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(uofmApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching UOFMs:', error);
    yield put(setError(error.message || 'Failed to search UOFMs'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch statistics
function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(uofmApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

// Bulk activate
function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(uofmApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(uofmApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'UOFMs activated successfully'));
      // Refresh the list
      yield put({ type: FETCH_UOFMS });
    } else {
      yield put(setError(response.message || 'Failed to activate UOFMs'));
    }
  } catch (error: any) {
    console.error('Error bulk activating UOFMs:', error);
    yield put(setError(error.message || 'Failed to activate UOFMs'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk deactivate
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(uofmApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(uofmApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'UOFMs deactivated successfully'));
      // Refresh the list
      yield put({ type: FETCH_UOFMS });
    } else {
      yield put(setError(response.message || 'Failed to deactivate UOFMs'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating UOFMs:', error);
    yield put(setError(error.message || 'Failed to deactivate UOFMs'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk delete
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(uofmApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(uofmApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'UOFMs deleted successfully'));
      // Refresh the list
      yield put({ type: FETCH_UOFMS });
    } else {
      yield put(setError(response.message || 'Failed to delete UOFMs'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting UOFMs:', error);
    yield put(setError(error.message || 'Failed to delete UOFMs'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Export to CSV
function* exportToCSVSaga(action: PayloadAction<UOFMSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { searchParams } = state.uofm;
    
    const params = {
      ...searchParams,
      ...action.payload,
    };
    
    const blob = yield call(uofmApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uofms_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('UOFMs exported successfully'));
  } catch (error: any) {
    console.error('Error exporting UOFMs:', error);
    yield put(setError(error.message || 'Failed to export UOFMs'));
  } finally {
    yield put(setLoading(false));
  }
}

// Root UOFM Saga
export function* uofmSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_UOFMS, fetchUOFMsSaga);
  yield takeEvery(FETCH_UOFM_BY_ID, fetchUOFMByIdSaga);
  yield takeEvery(CREATE_UOFM, createUOFMSaga);
  yield takeEvery(UPDATE_UOFM, updateUOFMSaga);
  yield takeEvery(DELETE_UOFM, deleteUOFMSaga);
  yield takeEvery(SEARCH_UOFMS, searchUOFMsSaga);
  yield takeEvery(FETCH_STATISTICS, fetchStatisticsSaga);
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
} 